import { useMutation, useQuery } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import { listEngagementCustomerFiles, type EngagementFileOut } from '@/api/engagementFiles';
import { listEngagementNotes, type EngagementNoteOut } from '@/api/engagementNotes';
import { getInvoiceDownload } from '@/api/invoices';
import { getEngagementChecklist } from '@/api/onboardingChecklist';
import { EngagementIntakePanel } from '@/components/EngagementIntakePanel';
import { OnboardingChecklist } from '@/components/OnboardingChecklist';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/auth/AuthContext';
import { groupByCategory, UPLOAD_CATEGORIES } from '@/lib/portalOnboarding';

/** Practitioner-side visibility of what the customer submitted through
 *  the onboarding portal: the derived onboarding checklist, the intake
 *  questionnaire, uploads grouped by category (with raw download), and
 *  the read-only notes stream. */
export function EngagementCustomerVisibility({
  engagementId,
  customerSlug,
}: {
  engagementId: string;
  customerSlug: string;
}) {
  return (
    <section className="grid gap-3">
      <h3 className="text-base font-semibold text-brand-navy">From the customer</h3>
      <EngagementChecklist engagementId={engagementId} />
      <EngagementIntakePanel engagementId={engagementId} />
      <CustomerUploads engagementId={engagementId} customerSlug={customerSlug} />
      <CustomerNotes engagementId={engagementId} />
    </section>
  );
}

function EngagementChecklist({ engagementId }: { engagementId: string }) {
  const { userKey, getIdToken } = useAuth();
  const checklist = useQuery({
    queryKey: ['engagementChecklist', engagementId, userKey],
    queryFn: async () => getEngagementChecklist(engagementId, (await getIdToken()) ?? ''),
  });
  if (!checklist.data) return null;
  return (
    <OnboardingChecklist
      data={checklist.data}
      title="Onboarding checklist"
      description="Identical to what the customer sees in the portal."
    />
  );
}

function CustomerUploads({
  engagementId,
  customerSlug,
}: {
  engagementId: string;
  customerSlug: string;
}) {
  const { userKey, getIdToken } = useAuth();

  const files = useQuery({
    queryKey: ['engagementCustomerFiles', engagementId, userKey],
    queryFn: async () => listEngagementCustomerFiles(engagementId, (await getIdToken()) ?? ''),
  });

  // The download URL is fetched async, so the tab must be pre-opened
  // synchronously inside the click gesture — opening it after the fetch
  // resolves would be blocked by popup blockers. We open a blank tab on
  // click, sever `opener` for security, then navigate it once the
  // presigned URL is in hand (or close it on failure).
  const download = useMutation({
    mutationFn: async ({ documentId }: { documentId: string; win: Window | null }) => {
      const res = await getInvoiceDownload(customerSlug, documentId, (await getIdToken()) ?? '');
      return res.url;
    },
    onSuccess: (url, { win }) => {
      if (win) win.location.href = url;
      else window.open(url, '_blank', 'noopener,noreferrer');
    },
    onError: (_err, { win }) => {
      win?.close();
    },
  });

  const startDownload = (documentId: string) => {
    const win = window.open('', '_blank');
    if (win) win.opener = null;
    download.mutate({ documentId, win });
  };

  const byCategory = groupByCategory(files.data?.items ?? []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Customer uploads</CardTitle>
        <CardDescription>
          Files the customer submitted through the onboarding portal, grouped by category. Raw
          download opens the original bytes.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {files.isPending ? (
          <p className="text-sm text-brand-slate">Loading customer uploads…</p>
        ) : files.isError ? (
          <p className="text-sm text-destructive">Couldn&apos;t load customer uploads.</p>
        ) : (files.data.items.length === 0 ? (
          <p className="text-sm text-brand-slate">No customer uploads yet.</p>
        ) : (
          UPLOAD_CATEGORIES.map((meta) => {
            const items = byCategory[meta.category];
            return (
              <div key={meta.category} className="grid gap-2">
                <div className="flex items-baseline justify-between">
                  <h4 className="text-sm font-medium text-brand-navy">{meta.label}</h4>
                  <span className="text-xs text-brand-slate">
                    {items.length === 0 ? 'None' : `${items.length}`}
                  </span>
                </div>
                {items.length > 0 && (
                  <ul className="grid gap-2">
                    {items.map((f) => (
                      <CustomerFileRow
                        key={f.id}
                        file={f}
                        onDownload={() => startDownload(f.id)}
                        downloading={download.isPending && download.variables?.documentId === f.id}
                      />
                    ))}
                  </ul>
                )}
              </div>
            );
          })
        ))}
        {download.isError && (
          <p className="text-xs text-destructive">
            Download failed —{' '}
            {download.error instanceof ApiError ? `HTTP ${download.error.status}` : 'see console.'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function CustomerFileRow({
  file,
  onDownload,
  downloading,
}: {
  file: EngagementFileOut;
  onDownload: () => void;
  downloading: boolean;
}) {
  return (
    <li className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2 text-sm">
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-brand-navy">{file.filename}</div>
        <div className="text-xs text-brand-slate">
          Customer · {formatBytes(file.byte_count)} · uploaded{' '}
          {new Date(file.uploaded_at).toLocaleString()}
        </div>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={onDownload}
        disabled={downloading}
      >
        {downloading ? 'Opening…' : 'Download'}
      </Button>
    </li>
  );
}

function CustomerNotes({ engagementId }: { engagementId: string }) {
  const { userKey, getIdToken } = useAuth();
  const notes = useQuery({
    queryKey: ['engagementNotes', engagementId, userKey],
    queryFn: async () => listEngagementNotes(engagementId, (await getIdToken()) ?? ''),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Customer notes</CardTitle>
        <CardDescription>Notes the customer left during onboarding. Newest first.</CardDescription>
      </CardHeader>
      <CardContent>
        {notes.isPending ? (
          <p className="text-sm text-brand-slate">Loading notes…</p>
        ) : notes.isError ? (
          <p className="text-sm text-destructive">Couldn&apos;t load notes.</p>
        ) : notes.data.items.length === 0 ? (
          <p className="text-sm text-brand-slate">No notes yet.</p>
        ) : (
          <ul className="grid gap-2">
            {notes.data.items.map((note) => (
              <NoteRow key={note.id} note={note} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function NoteRow({ note }: { note: EngagementNoteOut }) {
  return (
    <li className="rounded-md border border-border px-3 py-2">
      <p className="whitespace-pre-wrap text-sm text-brand-navy">{note.body}</p>
      <p className="mt-1 text-xs capitalize text-brand-slate">
        {note.author_kind} · {new Date(note.created_at).toLocaleString()}
      </p>
    </li>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
